import { useState, useMemo } from 'react'
import { useTablesQuery } from '@/hooks/useDatabase'
import type { DataSource } from '@/services/api/models/DataSource'
import type { AgentProxyGetTablesResponse } from '@/services/api/models/AgentProxyGetTablesResponse'
import {
  ChartBarIcon,
  TableCellsIcon,
  EyeIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'

// Define Table type from the response structure
type Table = NonNullable<AgentProxyGetTablesResponse['tables']>[0]

interface TableSizeViewProps {
  connectionId: string
  database: string
  schema?: string
  dataSource: DataSource
}

type SortField = 'name' | 'size' | 'rows'
type SortDirection = 'asc' | 'desc'

interface TableWithParsedSize extends Table {
  sizeBytes?: number
}

export function TableSizeView({ connectionId, database, schema, dataSource }: TableSizeViewProps) {
  const [sortField, setSortField] = useState<SortField>('size')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [searchTerm, setSearchTerm] = useState('')

  const { data: tables, isLoading } = useTablesQuery(connectionId, database, schema, {
    databaseType: dataSource.databaseType
  })

  // Parse size values for sorting and filtering
  const tablesWithParsedSize = useMemo(() => {
    if (!tables) return []
    
    const tablesArray = Array.isArray(tables.data) ? tables.data : []
    
    return tablesArray.map((table: Table): TableWithParsedSize => {
      let sizeBytes = 0
      
      if (table.size) {
        const match = /^([\d.]+)\s*(\w+)$/i.exec(table.size)
        if (match) {
          const value = Number.parseFloat(match[1])
          const unit = match[2].toLowerCase()
          
          switch (unit) {
            case 'b':
            case 'bytes':
              sizeBytes = value
              break
            case 'kb':
            case 'kilobytes':
              sizeBytes = value * 1024
              break
            case 'mb':
            case 'megabytes':
              sizeBytes = value * 1024 * 1024
              break
            case 'gb':
            case 'gigabytes':
              sizeBytes = value * 1024 * 1024 * 1024
              break
            case 'tb':
            case 'terabytes':
              sizeBytes = value * 1024 * 1024 * 1024 * 1024
              break
            default:
              sizeBytes = value
          }
        }
      }
      
      return {
        ...table,
        sizeBytes,
      }
    })
  }, [tables])

  // Filter and sort tables
  const processedTables = useMemo(() => {
    let filtered = tablesWithParsedSize
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((table: TableWithParsedSize) => 
        table.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }
    
    // Apply sorting
    filtered.sort((a: TableWithParsedSize, b: TableWithParsedSize) => {
      let aValue: number | string
      let bValue: number | string
      
      switch (sortField) {
        case 'name':
          aValue = a.name || ''
          bValue = b.name || ''
          break
        case 'size':
          aValue = a.sizeBytes || 0
          bValue = b.sizeBytes || 0
          break
        case 'rows':
          aValue = a.rows || 0
          bValue = b.rows || 0
          break
        default:
          return 0
      }
      
      let comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue)
      } else {
        const aNum = typeof aValue === 'number' ? aValue : Number(aValue)
        const bNum = typeof bValue === 'number' ? bValue : Number(bValue)
        comparison = aNum - bNum
      }
      
      return sortDirection === 'asc' ? comparison : -comparison
    })
    
    return filtered
  }, [tablesWithParsedSize, searchTerm, sortField, sortDirection])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  // Calculate statistics
  const stats = useMemo(() => {
    if (!processedTables.length) return null
    
    const totalSize = processedTables.reduce((sum: number, table: TableWithParsedSize) => sum + (table.sizeBytes || 0), 0)
    const totalRows = processedTables.reduce((sum: number, table: TableWithParsedSize) => sum + (table.rows || 0), 0)
    const maxSize = Math.max(...processedTables.map((t: TableWithParsedSize) => t.sizeBytes || 0))
    
    return {
      totalTables: processedTables.length,
      totalSize,
      totalRows,
      maxSize,
    }
  }, [processedTables])

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B'
    
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    
    return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`
  }

  const getSizeBarWidth = (sizeBytes: number) => {
    if (!stats?.maxSize || stats.maxSize === 0) return '0%'
    return `${(sizeBytes / stats.maxSize) * 100}%`
  }

  const getSizeColor = (sizeBytes: number) => {
    const sizeMB = sizeBytes / (1024 * 1024)
    
    if (sizeMB < 1) return 'bg-green-400'
    if (sizeMB < 10) return 'bg-yellow-400'
    if (sizeMB < 100) return 'bg-orange-400'
    return 'bg-red-400'
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="flex-shrink-0 p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <ChartBarIcon className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Table Size Analysis
              </h2>
              <p className="text-sm text-gray-500">
                {database}{schema && `.${schema}`} • {dataSource.displayName}
              </p>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tables..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value) }}
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Statistics */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
              <div className="text-sm font-medium text-blue-600 dark:text-blue-400">Tables</div>
              <div className="text-xl font-bold text-blue-900 dark:text-blue-100">
                {stats.totalTables}
              </div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
              <div className="text-sm font-medium text-green-600 dark:text-green-400">Total Size</div>
              <div className="text-xl font-bold text-green-900 dark:text-green-100">
                {formatBytes(stats.totalSize)}
              </div>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
              <div className="text-sm font-medium text-purple-600 dark:text-purple-400">Total Rows</div>
              <div className="text-xl font-bold text-purple-900 dark:text-purple-100">
                {stats.totalRows.toLocaleString()}
              </div>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 p-3 rounded-lg">
              <div className="text-sm font-medium text-orange-600 dark:text-orange-400">Avg Size</div>
              <div className="text-xl font-bold text-orange-900 dark:text-orange-100">
                {stats.totalTables > 0 ? formatBytes(stats.totalSize / stats.totalTables) : '0 B'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
            <tr>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                onClick={() => { handleSort('name') }}
              >
                <div className="flex items-center space-x-1">
                  <span>Table Name</span>
                  {sortField === 'name' && (
                    sortDirection === 'asc' ? 
                      <ArrowUpIcon className="w-3 h-3" /> : 
                      <ArrowDownIcon className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Type
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                onClick={() => { handleSort('rows') }}
              >
                <div className="flex items-center space-x-1">
                  <span>Rows</span>
                  {sortField === 'rows' && (
                    sortDirection === 'asc' ? 
                      <ArrowUpIcon className="w-3 h-3" /> : 
                      <ArrowDownIcon className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                onClick={() => { handleSort('size') }}
              >
                <div className="flex items-center space-x-1">
                  <span>Size</span>
                  {sortField === 'size' && (
                    sortDirection === 'asc' ? 
                      <ArrowUpIcon className="w-3 h-3" /> : 
                      <ArrowDownIcon className="w-3 h-3" />
                  )}
                </div>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Visual
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {processedTables.map((table: TableWithParsedSize) => {
              const isView = table.type.toUpperCase().includes('VIEW')
              
              return (
                <tr 
                  key={table.name}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      {isView ? (
                        <EyeIcon className="w-4 h-4 mr-2 text-purple-600" />
                      ) : (
                        <TableCellsIcon className="w-4 h-4 mr-2 text-green-600" />
                      )}
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {table.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={clsx(
                      "inline-flex px-2 py-1 text-xs font-semibold rounded-full",
                      isView 
                        ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                        : "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                    )}>
                      {isView ? 'View' : 'Table'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {table.rows.toLocaleString() || '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {table.size || '—'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {table.sizeBytes && table.sizeBytes > 0 ? (
                      <div className="w-24 h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                        <div 
                          className={clsx("h-full rounded-full transition-all", getSizeColor(table.sizeBytes))}
                          style={{ width: getSizeBarWidth(table.sizeBytes) }}
                        />
                      </div>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {processedTables.length === 0 && (
          <div className="text-center py-12">
            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              {searchTerm ? 'No matching tables' : 'No tables found'}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search term.' : 'This database appears to be empty.'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
